import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, ViewEncapsulation, inject } from '@angular/core';
import { EditInfoDialogComponent } from '@components/profile-account/edit-info-dialog/edit-info-dialog.component';
import { UserPendingLabelComponent } from '@components/user-pending-label/user-pending-label.component';
import { TranslocoModule } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { ApiCallInjectDirective } from 'src/app/common/directives/api-call-inject.directive';
import { ApiSignal, WritableApiSignal, refetchApiSignal, toApiSignal, toWritableApiSignal } from 'src/app/common/util/api-signal';
import { DialogService } from 'src/app/common/util/dialog-service';
import { Events } from 'src/app/util/events';
import { userInfoInputFields } from 'src/app/util/user-info-input-fields';
import { UserInfoInputConfig } from 'src/app/util/user-info-input-groupt';
import { ShadowStylesDirective } from 'src/boilerplate/shadow-styles.directive';
import { ColleagueInfo, ColleaguesService, UserInfo, UserService } from 'src/openapi-client';
import { ColleagueDisplayComponent } from '../colleague-display/colleague-display.component';
import { RecertificationPopupComponent } from './recertification-popup/recertification-popup.component';

@Component({
  selector: 'wct-recertification',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ColleagueDisplayComponent,
    UserPendingLabelComponent,
    ApiCallInjectDirective,
  ],
  templateUrl: './recertification.component.html',
  styleUrls: ['./recertification.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  hostDirectives: [ShadowStylesDirective],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class RecertificationComponent {
  private readonly colleaguesService: ColleaguesService = inject(ColleaguesService);
  private readonly userService: UserService = inject(UserService);
  private readonly dialogService: DialogService = inject(DialogService);

  protected readonly colleagues: WritableApiSignal<ColleagueInfo[]> = toWritableApiSignal(this.colleaguesService.loadColleagues());
  protected fundsLoading: boolean = false;

  protected readonly userInfo: ApiSignal<UserInfo> = toApiSignal(this.userService.loadUser());

  constructor() {
    Events.colleaguesModified.observable().subscribe(() => {
      refetchApiSignal(this.colleaguesService.loadColleagues(), this.colleagues, false);
    });
  }

  protected recertificate(): void {
    this.dialogService.create(RecertificationPopupComponent);
  }

  protected createUser(): void {
    const newUser: UserInfo = this.createEmptyUserInfo();
    this.dialogService.create(EditInfoDialogComponent, {
      inputConfig: new UserInfoInputConfig(
        [
          { inputs: userInfoInputFields.common, titleI18n: userInfoInputFields.commonI18n },
          { inputs: userInfoInputFields.personal, titleI18n: userInfoInputFields.personalI18n },
          { inputs: userInfoInputFields.businessReadonly, titleI18n: userInfoInputFields.businessI18n },
          { inputs: userInfoInputFields.additional, titleI18n: userInfoInputFields.additionalI18n }
        ],
        userInfoInputFields.personalCombinedRequiredInputs
      ),
      userInfo: newUser,
      saveI18n: 'profile.createUserDialog.saveText',
      titleI18n: 'profile.createUserDialog.title',
      successI18n: 'profile.createUserDialog.success',
      saveAction: this.saveUserInfo,
    });
  }

  private createEmptyUserInfo() {
    const emptyUser: UserInfo = userInfoInputFields.copy(this.userInfo()!, userInfoInputFields.business);

    const splitMail: string[] | undefined = this.userInfo()?.mailAddress.split('@');
    if (splitMail && splitMail.length === 2) {
      emptyUser.mailAddress = '@' + splitMail[1];
    }

    return emptyUser;
  }

  private readonly saveUserInfo = async (user: UserInfo): Promise<void> => {
    await firstValueFrom(this.userService.createUser(user));
    Events.colleaguesModified.next();
  }
}
